'use client';

import * as React from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export type StatCardProps = {
  value: React.ReactNode;
  label: React.ReactNode;
  icon?: LucideIcon;
  /** Render as a link with an ArrowRight affordance in the header. */
  href?: string;
  onClick?: () => void;
  className?: string;
};

/**
 * Vertical stat card matching the /d home-page pattern:
 *   border-t burgundy accent, icon + arrow at the top,
 *   extrabold tabular number, uppercase wide-tracked caption.
 */
export function StatCard({
  value,
  label,
  icon: Icon,
  href,
  onClick,
  className,
}: StatCardProps) {
  const base = cn(
    'group flex flex-col gap-1 p-3 sm:p-4 bg-card border border-border border-t border-t-primary transition-all',
    (href || onClick) && 'hover:border-primary/60',
    className,
  );
  const style = { boxShadow: 'var(--shadow-warm-sm)' as const };

  const inner = (
    <>
      <div className="flex items-center justify-between">
        {Icon ? <Icon className="h-3.5 w-3.5 text-primary" aria-hidden="true" /> : <span />}
        {(href || onClick) && (
          <ArrowRight
            className="h-3 w-3 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all"
            aria-hidden="true"
          />
        )}
      </div>
      <p className="stat-value text-foreground mt-1">{value}</p>
      <p className="text-overline text-muted-foreground">{label}</p>
    </>
  );

  if (href) {
    return (
      <Link href={href} className={base} style={style}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(base, 'text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30')} style={style}>
        {inner}
      </button>
    );
  }
  return (
    <div className={base} style={style}>
      {inner}
    </div>
  );
}
